import { useCallback, useEffect, useState } from 'react'
import api from '../../../../../services/api'
import Form, { Textarea } from '../../../../../components/Form'
import { useToast } from '../../../../../hooks/toast'
import { useLoading } from '../../../../../hooks/loading'
import {
  apiSendMessageToAllClients,
  apiSendMessageByMenuFoods
} from '../../domain/api'
import { Loading } from 'components/Loading'

type SendMessageByMenuFoodsProps = {
  id: number
  handleClickOnCloseModal: () => void
}

export const SendMessageByMenuFoods = ({
  id,
  handleClickOnCloseModal
}: SendMessageByMenuFoodsProps): JSX.Element => {
  const { addToast } = useToast()
  const { activeLoading, disableLoading } = useLoading()
  const [loading, setLoading] = useState(false)

  const [menuFoods, setMenuFoods] = useState<IMenuFoodData[]>([])
  const [menuFoodsIds, setMenuFoodsIds] = useState<number[]>([])
  const [sendToAllClients, setSendToAllClients] = useState(0)

  const getMenuFoodsByInstitution = useCallback(async (id: number) => {
    const { data } = await api.get<IMenuFoodData[]>(
      `commercial/menuFoods/listOptionsActiveByInstitution/${id}`
    )

    setMenuFoods(data)
  }, [])

  const getMenuFoods = useCallback(async () => {
    const { data } = await api.get<IMenuFoodData[]>(
      'commercial/menuFoods/listOptionsActiveWithInstitutions'
    )

    setMenuFoods(data)
  }, [])

  useEffect(() => {
    if (id) {
      getMenuFoodsByInstitution(id)
    } else {
      getMenuFoods()
    }
  }, [getMenuFoodsByInstitution, getMenuFoods, id])

  const onSubmitForm = async (data: IClientData) => {
    if (!menuFoodsIds.length && sendToAllClients === 0) {
      addToast({
        type: 'error',
        title: 'Por favor, selecione pelo menos um cardápio.'
      })
      return
    }
    activeLoading()
    setLoading(true)
    try {
      if (sendToAllClients === 1) {
        await api.post(apiSendMessageToAllClients(), {
          ...data,
          institution_id: id ? Number(id) : undefined
        })
      } else {
        await api.post(apiSendMessageByMenuFoods(), {
          ...data,
          institution_id: id ? Number(id) : undefined,
          menuFoodsIds
        })
      }
      handleClickOnCloseModal()
      disableLoading()
      setLoading(false)
      addToast({
        type: 'success',
        title: 'Mensagem enviada',
        description: 'Mensagem enviada com sucesso'
      })
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Tente novamente'
      addToast({
        type: 'error',
        title: 'Erro ao enviar mensagem para o cliente',
        description: message
      })
      handleClickOnCloseModal()
      disableLoading()
      setLoading(false)
    }
  }

  const addMenuFoodId = (menuFoodId: number) => {
    setMenuFoodsIds(old => [...old, menuFoodId])
  }

  const removeMenuFoodId = (menuFoodIdToDelete: number) => {
    setMenuFoodsIds(old => {
      return old.filter(menuFoodId => menuFoodId !== menuFoodIdToDelete)
    })
  }

  const addAllMenuFood = () => {
    const allMenuFood = menuFoods.map(menuFood => menuFood.id)
    setMenuFoodsIds(allMenuFood)
  }

  const removeAllMenuFood = () => {
    setMenuFoodsIds([])
  }

  if (!menuFoods.length) {
    return (
      <div>
        <Loading isActive />
      </div>
    )
  }
  return (
    <Form onSubmit={onSubmitForm}>
      <>
        <div className="row mb-6">
          <p className="mb-2">Selecione os cardápios: </p>
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <input
              type="checkbox"
              name={'menu_foods_ids_select_all'}
              id={'menu_foods_ids_select_all'}
              onChange={e =>
                e.target.checked ? addAllMenuFood() : removeAllMenuFood()
              }
            />
            <label
              htmlFor={'menu_foods_ids_select_all'}
              className="mx-2 my-2 fw-bold"
            >
              Selecionar tudo
            </label>
          </div>

          {menuFoods?.map(menuFood => (
            <div
              key={menuFood.id}
              style={{ display: 'flex', alignItems: 'baseline' }}
            >
              <input
                type="checkbox"
                name={`menu_foods_ids${menuFood.id}`}
                id={`menu_foods_ids${menuFood.id}`}
                value={menuFood.id}
                onChange={e =>
                  e.target.checked
                    ? addMenuFoodId(Number(e.target.value))
                    : removeMenuFoodId(Number(e.target.value))
                }
                checked={menuFoodsIds.includes(menuFood.id)}
              />
              <label htmlFor={`menu_foods_ids${menuFood.id}`} className="mx-2">
                {menuFood.menu_food_type?.name}
                {menuFood.menu_institutions && (
                  <span style={{ marginLeft: '10px' }}>
                    (
                    {menuFood.menu_institutions
                      ?.map(mi => mi?.institution?.company_social_name)
                      ?.join(', ')}
                    )
                  </span>
                )}
              </label>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'baseline' }}>
            <input
              type="checkbox"
              name={'send_to_institution'}
              id={'send_to_institution'}
              value={sendToAllClients}
              onChange={e =>
                e.target.checked
                  ? setSendToAllClients(1)
                  : setSendToAllClients(0)
              }
              checked={!!sendToAllClients}
            />
            <label
              htmlFor={'send_to_institution'}
              className="mx-2 my-8 fw-bold"
              style={{ fontSize: '14px' }}
            >
              Enviar para todos os clientes da instituição (em todo o período,
              clientes com contrato ativo ou não)
            </label>
          </div>
        </div>
        <div className="row mb-5">
          <Textarea
            name="message"
            className=" col-md-12"
            label="Mensagem"
            rows={3}
            rules={{ required: true }}
          />
        </div>
        <div className="card-footer d-flex justify-content-end py-6 px-9">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            Enviar
          </button>
        </div>
      </>
    </Form>
  )
}
